// Resolves Poku TV configuration with this precedence:
//   1. window.POKU_CONFIG (public/config.js — editable after deploy)
//   2. VITE_* build-time environment variables
//   3. defaults
//
// Modes:
//   direct — tmdbToken is set: the app calls TMDB directly with the token.
//   proxy  — tmdbToken is empty: the app calls the same-origin /api/tmdb/
//            path, where a serverless function injects the token server-side.
const runtime = typeof window !== "undefined" ? window.POKU_CONFIG || {} : {};

const env = import.meta.env || {};

const rawBase = runtime.tmdbBaseUrl || env.VITE_TMDB_BASE_URL || "";
const rawToken = runtime.tmdbToken || env.VITE_TMDB_TOKEN || "";

const defaultBase = "https://api.themoviedb.org/3/";
const normalizedBase = rawBase.endsWith("/") ? rawBase : rawBase ? `${rawBase}/` : "";

// ---------------------------------------------------------------------------
// Cross-origin API base (packaged apps)
// ---------------------------------------------------------------------------
// The web deploy is served from the SAME origin as its /api/* functions, so
// apiBase stays "" and every API path stays relative — no CORS anywhere,
// exactly as before this existed.
//
// The Capacitor iOS app bundles this SPA and loads it from
// capacitor://localhost, which is a DIFFERENT origin from those functions: a
// relative fetch("/api/library") there resolves against the app bundle and
// 404s. So when we detect that shell, apiBase becomes the absolute Netlify
// origin and apiUrl() (plus the fetch/XHR bridge in src/native-api.js) routes
// API calls back to it.
//
// Media URLs handed straight to <video>/hls.js never go through fetch, so the
// bridge cannot see them — those are absolutised with apiUrl() where they are
// built instead (see playback/providers.js and screens/LiveTVScreen.jsx).
// Relative URLs *inside* an HLS playlist are not a problem: AVFoundation and
// hls.js resolve them against the playlist's own (now absolute) URL, which is
// how the server's rewritten /api/hosted/media chain keeps working.
const NATIVE_API_ORIGIN = "https://poku-tv-web.netlify.app";

const isNativeApp = (() => {
  if (typeof window === "undefined") return false;
  try {
    // Capacitor injects window.Capacitor into its WebView; the scheme check is
    // the fallback for the moment before the native bridge is defined.
    if (window.Capacitor?.isNativePlatform?.() === true) return true;
    return typeof location !== "undefined" && location.protocol === "capacitor:";
  } catch {
    return false;
  }
})();

// "" on the web; the absolute origin in the packaged app. Overridable through
// window.POKU_CONFIG.apiBase (public/config.js) for a self-hosted deployment.
export const apiBase =
  String(runtime.apiBase || "").replace(/\/+$/, "") || (isNativeApp ? NATIVE_API_ORIGIN : "");

/**
 * Resolve a same-origin API path against the API origin. Absolute URLs (a host
 * CDN, TMDB direct mode, an embed page) pass through untouched.
 */
export const apiUrl = (path) => {
  const value = String(path);
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `${apiBase}${value}`;
};

// ---------------------------------------------------------------------------
// Pi relay (StreamTape playback + subtitles)
// ---------------------------------------------------------------------------
// StreamTape serves a signed PROGRESSIVE mp4 the browser can play straight off
// their CDN, which is what the hosted provider has handed <video> since
// 2026-09-26. The relay on the Pi (web/poku-stream-relay.mjs) sits in front of
// that same file and adds the two things the CDN cannot:
//
//   - subtitles. StreamTape flattens every upload, so the file carries none,
//     and the public subtitle store has no track for several mapped titles
//     (verified 2026-10-01: /api/subs/get for Padavettu returns zero tracks,
//     while the Pi's own store holds the English one). The relay serves them
//     from that store, with release-scene advert cues stripped at serve time.
//   - a stable URL. The signed CDN link expires in ~24 h and has to be minted
//     per playback; the relay URL is derived from the file id and does not.
//
// EMPTY IS THE DEFAULT AND MEANS OFF: playback then uses the signed CDN URL
// exactly as it does today, and nothing about this file changes behaviour until
// a base URL is set in public/config.js. The relay binds 127.0.0.1, so the value
// has to be a public hostname that routes to it (a Cloudflare tunnel ingress) —
// which is why it lives in config rather than in code: the hostname can change
// without a rebuild, and the temporary trycloudflare name that was used while
// testing must never be baked into a bundle.
const rawRelay = String(runtime.piRelayBase || env.VITE_PI_RELAY_BASE || "").trim();

// Only an http(s) origin is accepted. A junk or hostile value (javascript:,
// data:) must not reach <video src>, and silently ignoring it is right: the
// result is the pre-relay behaviour, which works.
export const piRelayBase = (() => {
  if (!rawRelay) return "";
  try {
    const u = new URL(rawRelay);
    if (u.protocol !== "http:" && u.protocol !== "https:") return "";
    return `${u.origin}${u.pathname.replace(/\/+$/, "")}`;
  } catch {
    return "";
  }
})();

/** Absolute relay URL for a path, or "" when no relay is configured. */
export const piRelayUrl = (path) => (piRelayBase ? `${piRelayBase}${path}` : "");

export const config = {
  // TMDB-compatible API root, always trailing "/".
  tmdbBaseUrl: normalizedBase || defaultBase,
  // Empty string means proxy mode.
  tmdbToken: String(rawToken || ""),
  // Path the metadata proxy is mounted at. Same-origin on the web; prefixed
  // with the API origin in the packaged app so apiFetch() stays a one-liner.
  proxyPath: apiUrl(runtime.proxyPath || "/api/tmdb/").replace(/\/?$/, "/"),
  // Preferred playback provider id (see src/playback/registry.js).
  // "hosted" = the admin-mapped package. VidLink was unwired 2026-09-24.
  preferredPlaybackProvider: runtime.preferredPlaybackProvider || "hosted",
  // Public base URL of the Pi relay, or "" when it is not configured (= off).
  // See the long note above piRelayBase.
  piRelayBase,
  // VidLink is unwired (2026-09-24), so this flag no longer affects playback.
  // It is kept because the same /embed/* proxy it used is still load-bearing:
  // the reserve tier is served through it as /embed/reserve/<key>/... (see
  // web/reserve-embeds.js). Restoring VidLink would make this meaningful again.
  vidkingProxy: runtime.vidkingProxy === true,
  // Ordered reserve embed hosts — the six third-party backups that sit behind
  // SuperEmbed (see src/playback/registry.js and web/reserve-embeds.js).
  // These domains rotate or die often, so the list lives in config: reorder it
  // to change the fallback order, or remove a key to retire a host, without
  // touching code. Keys must exist in the server allowlist (web/reserve-embeds.js)
  // or they are ignored. An empty array disables the tier entirely.
  reserveEmbeds: Array.isArray(runtime.reserveEmbeds)
    ? runtime.reserveEmbeds
    // cinesrc leads: the one host with documented embed routes, a documented
    // customization query set and a documented postMessage event/command API
    // (https://cinesrc.st/docs).
    : ["cinesrc", "vidbolt", "vidsrc", "vidnest", "vidcore", "streamrip"],
};

export const isProxyMode = config.tmdbToken.length === 0;

// Base URL used for API calls. In proxy mode the app uses a same-origin path,
// which also avoids any CORS concerns on hosts that restrict API access.
export const apiBaseUrl = isProxyMode ? config.proxyPath : config.tmdbBaseUrl;

// True when the site clearly has no TMDB connection configured at all.
// In direct mode that means no token; in proxy mode the proxy is reachable
// but may still report that it is unconfigured (handled by the API client).
export const isConfigured = true;